/**
 * Custom hook for managing dialog states
 * Handles common dialog patterns: main dialog, delete confirmation, editing states
 */

import { useState } from 'react';

interface UseDialogStateReturn<T> {
  dialogOpen: boolean;
  editingItem: T | null;
  isEditing: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  openEditDialog: (item: T) => void;
  deleteConfirmOpen: boolean;
  itemToDelete: T | null;
  openDeleteConfirm: (item: T) => void;
  closeDeleteConfirm: () => void;
}

export const useDialogState = <T>(): UseDialogStateReturn<T> => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<T | null>(null);

  const openDialog = (): void => {
    setDialogOpen(true);
  };

  const closeDialog = (): void => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const openEditDialog = (item: T): void => {
    setEditingItem(item);
    setDialogOpen(true);
  };

  const openDeleteConfirm = (item: T): void => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = (): void => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const isEditing = editingItem !== null;

  return {
    // Main dialog state
    dialogOpen,
    editingItem,
    isEditing,
    openDialog,
    closeDialog,
    openEditDialog,

    // Delete confirmation state
    deleteConfirmOpen,
    itemToDelete,
    openDeleteConfirm,
    closeDeleteConfirm,
  };
};